import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Section IDs en la home (deben coincidir con navLinks en Header)
const HOME_SECTION_IDS = ['about', 'services', 'video', 'quote', 'reviews', 'carrier', 'faq'];

interface ScrollOptions {
  behavior?: ScrollBehavior;
  offset?: number;
}

export const useScrollNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isScrollingProgrammatically = useRef(false);
  const skipNextHashScroll = useRef(false);
  const lastProgrammaticSectionRef = useRef<{ id: string; time: number } | null>(null);

  // Función para scroll suave con offset personalizable
  const scrollToElement = useCallback((
    elementId: string, 
    options: ScrollOptions = {}
  ) => {
    const {
      behavior = 'smooth',
      offset = 0
    } = options;

    const element = document.getElementById(elementId);
    if (!element) return;

    const isMobile = window.innerWidth < 768;
    const baseOffset = isMobile ? 20 : 190;
    const totalOffset = baseOffset + offset;
    const rect = element.getBoundingClientRect();
    const elementPosition = rect.top + window.scrollY - totalOffset;

    window.scrollTo({
      top: Math.max(0, elementPosition),
      behavior
    });
  }, []);

  // When navigating to home with hash (e.g. from /contact), go to home first then apply hash so the right section is active
  const navigateToSection = useCallback((sectionId: string) => {
    lastProgrammaticSectionRef.current = { id: sectionId, time: Date.now() };
    if (location.pathname !== '/') {
      navigate(`/#${sectionId}`);
      return;
    }
    isScrollingProgrammatically.current = true;
    scrollToElement(sectionId);
    skipNextHashScroll.current = true;
    navigate(`/#${sectionId}`, { replace: true });
    setTimeout(() => {
      isScrollingProgrammatically.current = false;
    }, 1500);
  }, [location.pathname, navigate, scrollToElement]);

  // Efecto para manejar hash en la URL al cargar/cambiar ruta
  useEffect(() => {
    if (location.hash && location.pathname === '/') {
      if (skipNextHashScroll.current) {
        skipNextHashScroll.current = false;
        return;
      }
      const sectionId = location.hash.replace(/^#/, '');
      if (!sectionId) return;

      // Marcar como scroll programático para que scroll-spy no sobrescriba con #about
      lastProgrammaticSectionRef.current = { id: sectionId, time: Date.now() };
      isScrollingProgrammatically.current = true;
      setTimeout(() => { isScrollingProgrammatically.current = false; }, 1500);

      // Wait for the target section to be in the DOM (lazy-loaded components may not be ready)
      let attempts = 0;
      const maxAttempts = 30; // ~3 seconds total
      const tryScroll = () => {
        const element = document.getElementById(sectionId);
        if (element) {
          const isMobile = window.innerWidth < 768;
          const baseOffset = isMobile ? 20 : 190;
          const rect = element.getBoundingClientRect();
          const elementPosition = rect.top + window.scrollY - baseOffset;
          window.scrollTo({ top: Math.max(0, elementPosition), behavior: 'smooth' });
          return;
        }
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(tryScroll, 100);
        }
      };
      const timeoutId = setTimeout(tryScroll, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [location.hash, location.pathname]);

  // Scroll-spy: al hacer scroll, actualizar hash para que el nav muestre el ítem activo
  useEffect(() => {
    if (location.pathname !== '/') return;

    const headerOffset = 200;
    let ticking = false;
    let lastHash = location.hash.slice(1);

      const updateActiveSection = () => {
        const recent = lastProgrammaticSectionRef.current;
        if (recent && Date.now() - recent.time < 2500) {
          return;
        }
        const scrollY = window.scrollY || window.pageYOffset;
        let current = '';
        for (const id of HOME_SECTION_IDS) {
          const el = document.getElementById(id);
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          const top = rect.top + scrollY;
          if (top - headerOffset <= scrollY + 80) {
            current = id;
          }
        }
        if (current && current !== lastHash) {
          lastHash = current;
          if (!isScrollingProgrammatically.current) {
            navigate(`/#${current}`, { replace: true });
          }
        }
      };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateActiveSection();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname, location.hash, navigate]);

  // Función para cerrar menú móvil y navegar
  const handleMobileNavigation = useCallback((
    sectionId: string, 
    closeMobileMenu?: () => void
  ) => {
    if (closeMobileMenu) {
      closeMobileMenu();
      // Delay para permitir que la animación del menú termine
      setTimeout(() => navigateToSection(sectionId), 300);
    } else {
      navigateToSection(sectionId);
    }
  }, [navigateToSection]);

  return {
    scrollToElement,
    navigateToSection,
    handleMobileNavigation
  };
};